import { useState } from "react";
import { Link, useParams } from "react-router-dom";
import { motion } from "framer-motion";
import { toast } from "sonner";
import { ArrowLeft, Minus, Plus } from "lucide-react";
import { PRODUCTS, formatINR, getProduct } from "@/data/products";
import { useCart } from "@/context/CartContext";
import { Swatch, ProductCard } from "@/components/ProductCard";
import { MaskLines, Reveal, EASE } from "@/components/Reveal";

const Gallery = ({ product, finish, setFinish }) => (
  <div className="lg:sticky lg:top-24">
    <div className="relative aspect-[4/5] overflow-hidden rounded-sm bg-sand">
      {product.finishes.map((f, i) => (
        <motion.img
          key={f.id}
          src={f.image}
          alt={`${product.name} in ${f.name}`}
          initial={false}
          animate={{ opacity: i === finish ? 1 : 0, scale: i === finish ? 1 : 1.06 }}
          transition={{ duration: 0.9, ease: EASE }}
          className="absolute inset-0 h-full w-full object-cover"
        />
      ))}
    </div>
    <div className="mt-4 grid grid-cols-2 gap-4">
      {product.finishes.map((f, i) => (
        <button key={f.id} data-testid={`gallery-thumb-${f.id}`} onClick={() => setFinish(i)} className={`aspect-[5/4] overflow-hidden rounded-sm ring-1 ring-offset-2 ring-offset-bone transition-[box-shadow,opacity] ${i === finish ? "ring-ink" : "opacity-60 ring-transparent hover:opacity-100"}`}>
          <img src={f.image} alt={f.name} className="h-full w-full object-cover" />
        </button>
      ))}
    </div>
  </div>
);

const Details = ({ product, finish, setFinish }) => {
  const { add, setOpen } = useCart();
  const [qty, setQty] = useState(1);
  const f = product.finishes[finish];
  const onAdd = () => {
    add(product.slug, f.id, qty);
    toast(`${product.name} added to your bag`, { description: `${f.name} · Qty ${qty}` });
    setOpen(true);
  };
  return (
    <div className="lg:pt-6">
      <p className="text-[11px] uppercase tracking-[0.28em] text-muted">{product.type}</p>
      <h1 className="mt-4 font-display text-[clamp(3.5rem,8vw,7rem)] leading-[0.88]" data-testid="product-name">
        <MaskLines lines={[product.name]} delay={0.05} />
      </h1>
      <p className="mt-4 font-display text-2xl italic text-clay">{product.tagline}</p>
      <p className="mt-8 font-display text-4xl tabular-nums" data-testid="product-detail-price">{formatINR(product.price)}</p>
      <p className="mt-1 text-xs text-muted">Inclusive of taxes · Delivery arranged personally</p>
      <p className="mt-8 max-w-lg leading-relaxed text-muted">{product.description}</p>

      <div className="mt-10 border-t border-ink/15 pt-6">
        <p className="text-[11px] uppercase tracking-[0.2em]">Finish — <span className="text-muted" data-testid="selected-finish-name">{f.name}</span></p>
        <div className="mt-4 flex gap-2">
          {product.finishes.map((x, i) => (
            <Swatch key={x.id} finish={x} active={i === finish} onClick={() => setFinish(i)} testId={`detail-swatch-${x.id}`} size="h-9 w-9" />
          ))}
        </div>
      </div>

      <dl className="mt-8 grid grid-cols-2 gap-6 border-t border-ink/15 pt-6 text-sm">
        <div><dt className="text-[11px] uppercase tracking-[0.2em] text-muted">Size (W × H × D)</dt><dd className="mt-2">{product.size}</dd></div>
        <div><dt className="text-[11px] uppercase tracking-[0.2em] text-muted">Features</dt><dd className="mt-2">{product.features.join(" · ")}</dd></div>
      </dl>

      <div className="mt-10 flex flex-wrap items-center gap-4">
        <div className="inline-flex h-14 items-center rounded-full border border-ink/25">
          <button data-testid="detail-qty-minus" onClick={() => setQty((q) => Math.max(1, q - 1))} className="px-4" aria-label="Decrease"><Minus className="h-4 w-4" /></button>
          <span data-testid="detail-qty-value" className="w-6 text-center tabular-nums">{qty}</span>
          <button data-testid="detail-qty-plus" onClick={() => setQty((q) => q + 1)} className="px-4" aria-label="Increase"><Plus className="h-4 w-4" /></button>
        </div>
        <button data-testid="add-to-cart-button" onClick={onAdd} className="btn-primary h-14 flex-1 sm:flex-none sm:px-12">
          Add to bag — {formatINR(product.price * qty)}
        </button>
      </div>
    </div>
  );
};

export default function Product() {
  const { slug } = useParams();
  const product = getProduct(slug);
  const [finish, setFinish] = useState(0);
  if (!product) {
    return (
      <main className="grid min-h-screen place-items-center px-5 text-center">
        <div><p className="font-display text-5xl">Piece not found.</p><Link to="/" className="btn-primary mt-8">Back home</Link></div>
      </main>
    );
  }
  const related = PRODUCTS.filter((p) => p.slug !== slug && p.category === product.category).concat(PRODUCTS.filter((p) => p.category !== product.category)).slice(0, 3);
  return (
    <main className="mx-auto max-w-[1440px] px-5 pb-24 pt-24 md:px-10 md:pt-28" key={slug}>
      <Link to="/#collection" data-testid="back-to-collection" className="link-underline inline-flex items-center gap-2 text-[12px] uppercase tracking-[0.16em]">
        <ArrowLeft className="h-4 w-4" /> Collection
      </Link>
      <div className="mt-8 grid gap-12 lg:grid-cols-[1.1fr_1fr] lg:gap-20">
        <Gallery product={product} finish={finish} setFinish={setFinish} />
        <Details product={product} finish={finish} setFinish={setFinish} />
      </div>
      <section className="mt-32">
        <Reveal><h2 className="font-display text-4xl md:text-6xl">Pairs well <em className="text-clay">with</em></h2></Reveal>
        <div className="mt-12 grid gap-x-6 gap-y-14 sm:grid-cols-2 lg:grid-cols-3">
          {related.map((p, i) => <ProductCard key={p.slug} product={p} index={i} />)}
        </div>
      </section>
    </main>
  );
}
